import React, { useState, useEffect, useRef } from 'react';
import { Ban, ArrowBigUpDash, ArrowBigDownDash } from 'lucide-react';
import './OrderSummary.css';

const OrderSummary = ({ orderItems, onRemoveItem, onUpdateQuantity }) => {
  const [showScrollUp, setShowScrollUp] = useState(false);
  const [showScrollDown, setShowScrollDown] = useState(false);
  const scrollContainerRef = useRef(null);

  // Check scroll position and update indicators
  const handleScroll = () => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    
    // Show up arrow if we can scroll up
    setShowScrollUp(scrollTop > 10);
    
    // Show down arrow if we can scroll down
    setShowScrollDown(scrollTop < scrollHeight - clientHeight - 10);
  };

  // Update scroll indicators when content changes
  useEffect(() => {
    handleScroll();
  }, [orderItems]);

  // Add scroll event listener
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      // Initial check
      setTimeout(handleScroll, 100);
      
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, []);

  // Scroll functions
  const scrollUp = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ top: -100, behavior: 'smooth' });
    }
  };

  const scrollDown = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ top: 100, behavior: 'smooth' });
    }
  };
  const decreaseQuantity = (item) => {
    if (item.quantity > 1) {
      onUpdateQuantity(item.id, item.quantity - 1);
    }
  };

  const increaseQuantity = (item) => {
    onUpdateQuantity(item.id, item.quantity + 1);
  };

  // Function to get length class based on product name length
  const getLengthClass = (name) => {
    const length = name.length;
    if (length > 30) return 'very-long';
    if (length > 20) return 'long';
    return 'normal';
  };

  // Function to parse product name and extract size information
  const parseProductInfo = (productName) => {
    // Look for parentheses content as size information
    const sizeMatch = productName.match(/\(([^)]+)\)/);
    
    if (sizeMatch) {
      // Extract the main name (everything before the parentheses)
      const name = productName.substring(0, sizeMatch.index).trim();
      const size = sizeMatch[1];
      return { name, size };
    }
    
    // If no parentheses, check for common size patterns at the end
    const commonSizePatterns = [
      /\s+(por\s+\w+|kg|L|cc|gr?|unidad|pack\s+\d+u|entero|mediano|grande|chico|chica|individual|docena|media\s+docena)$/i,
      /\s+\d+\w+$/
    ];
    
    for (const pattern of commonSizePatterns) {
      const match = productName.match(pattern);
      if (match) {
        const name = productName.substring(0, match.index).trim();
        const size = match[0].trim();
        return { name, size };
      }
    }
    
    // If no size pattern found, return the full name and empty size
    return { name: productName, size: '' };
  };

  return (
    <div className="order-summary" ref={scrollContainerRef}>
      {/* Scroll indicators */}
      <button 
        className={`scroll-indicator scroll-up ${showScrollUp ? 'visible' : ''}`}
        onClick={scrollUp}
        title="Scroll up"
      >
        <ArrowBigUpDash size={20} />
      </button>
      
      <button 
        className={`scroll-indicator scroll-down ${showScrollDown ? 'visible' : ''}`}
        onClick={scrollDown}
        title="Scroll down"
      >
        <ArrowBigDownDash size={20} />
      </button>

      <div className="order-items">
        {orderItems.length === 0 ? (
          <div className="empty-order">
            <p>No hay productos en la orden</p>
          </div>
        ) : (
          orderItems.map(item => {
            const { name, size } = parseProductInfo(item.name);
            const lengthClass = getLengthClass(name);
            return (
              <div key={item.id} className="order-item">
                <div className="item-details">
                  <div className="item-info">
                    <div className="item-name" data-length={lengthClass}>{name}</div>
                    {size && <div className="item-size">{size}</div>}
                    {item.discount && (
                      <div className="item-discount">{item.discount}% Descuento</div>
                    )}
                  </div>
                <div className="quantity-controls">
                  <button 
                    className={`qty-btn ${item.quantity <= 1 ? 'disabled' : ''}`}
                    onClick={() => decreaseQuantity(item)}
                    disabled={item.quantity <= 1}
                  >
                    -
                  </button>
                  <span className="quantity">{item.quantity}</span>
                  <button 
                    className="qty-btn"
                    onClick={() => increaseQuantity(item)}
                  >
                    +
                  </button>
                </div>
                <div className="item-price">${Math.round(item.price * item.quantity)}</div>
                <button 
                  className="remove-btn"
                  onClick={() => onRemoveItem(item.id)}
                >
                  <Ban size={16} color="#e74c3c" />
                </button>
              </div>
            </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default OrderSummary;